import { useCallback, useEffect, useRef, useState } from 'react';

import { VirtualizedGrid, VirtualizedGridProps } from './types';

export function useVirtualizedGrid<T>({
  rows,
  row_height,
  height,
  overscan,
}: VirtualizedGridProps<T>): VirtualizedGrid<T> {
  const [scroll_top, setScrollTop] = useState(0);
  const [is_scrolling, setQuickScrolling] = useState(false);

  const frameRef = useRef<number>();
  const last_scroll_time = useRef(Date.now());
  const last_scroll_position = useRef(0);
  const scroll_timeout = useRef<NodeJS.Timeout>();
  const velocity_tracker = useRef<number[]>([]);

  const total_height = rows.length * row_height;
  const visible_row_count = Math.ceil(height / row_height);
  const start = Math.max(0, Math.floor(scroll_top / row_height) - overscan);
  const end = Math.min(rows.length, start + visible_row_count + overscan * 2);
  const visible_rows = rows.slice(start, end);
  const velocity =
    velocity_tracker.current.reduce((a, b) => a + b, 0) / velocity_tracker.current.length;

  useEffect(() => {
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (scroll_timeout.current) clearTimeout(scroll_timeout.current);
    };
  }, []);

  const checkForFastScroll = useCallback(
    (current_velocity: number) => {
      velocity_tracker.current.push(current_velocity);
      if (velocity_tracker.current.length > 3) {
        velocity_tracker.current.shift();
      }

      const avg_velocity =
        velocity_tracker.current.reduce((a, b) => a + b, 0) / velocity_tracker.current.length;
      const is_fast = avg_velocity > 3;

      if (is_fast && !is_scrolling) {
        setQuickScrolling(true);
      }

      if (is_fast && scroll_timeout.current) {
        clearTimeout(scroll_timeout.current);
      }

      if (is_fast) {
        scroll_timeout.current = setTimeout(() => {
          velocity_tracker.current = [];
          setQuickScrolling(false);
        }, 150);
      }
    },
    [is_scrolling]
  );

  const updateScroll = useCallback(
    (new_scroll_top: number) => {
      const current_time = Date.now();
      const time_delta = Math.max(1, current_time - last_scroll_time.current);
      const position_delta = Math.abs(new_scroll_top - last_scroll_position.current);
      const velocity = position_delta / time_delta;

      last_scroll_time.current = current_time;
      last_scroll_position.current = new_scroll_top;

      checkForFastScroll(velocity);

      setScrollTop(new_scroll_top);
    },
    [checkForFastScroll]
  );

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const new_scroll_top = e.currentTarget.scrollTop;

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }

      frameRef.current = requestAnimationFrame(() => {
        updateScroll(new_scroll_top);
      });
    },
    [updateScroll]
  );

  return {
    visible_rows,
    start_index: start,
    end_index: end,
    total_height,
    velocity: velocity > 5 ? overscan : velocity,
    onScroll,
    is_scrolling,
  };
}
